import { isString } from 'lodash-es';

import type { IContextMutationResult } from '@modules/core/services/query/types';
import {
  AFFILIATE_TRACKER_FIXED_PAYMENT_QUERY,
  AFFILIATES_APPROVED_FIXED_PAYMENTS_PERMISSIONS,
} from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/submodules/fixed-tracker-payments/constants';
import { useRestClient } from '@modules/core/services/api';
import { useContextMutation } from '@modules/core/services/query';
import { useToastStore } from '@modules/toast/store';

import type { IAffiliateShowFixedPaymentResponse, IUpdateFixedPaymentsResponse } from '../../../types';
import type { IFixedPaymentsHistoryChangesItemPayload, IFixedPaymentsHistoryChangesItemResponse } from '../types';
import {
  FIXED_TRACKER_PAYMENTS_HISTORY_CHANGES_ITEM_QUERY,
  FIXED_TRACKER_PAYMENTS_HISTORY_CHANGES_ITEM_UPDATE_API_URL,
  FIXED_TRACKER_PAYMENTS_HISTORY_CHANGES_ITEM_UPDATE_MUTATION,
} from '../constants';

export default function useUpdateFixedPaymentsHistoryItemMutation(): IContextMutationResult<
  IUpdateFixedPaymentsResponse,
  Error,
  IFixedPaymentsHistoryChangesItemPayload
> {
  const { fetchPut } = useRestClient();
  const { displayPresetToast } = useToastStore();

  return useContextMutation<IUpdateFixedPaymentsResponse, Error, IFixedPaymentsHistoryChangesItemPayload>({
    mutationKey: [FIXED_TRACKER_PAYMENTS_HISTORY_CHANGES_ITEM_UPDATE_MUTATION],
    mutationFn: ({ itemId, ...payload }) => fetchPut<IUpdateFixedPaymentsResponse>(
      FIXED_TRACKER_PAYMENTS_HISTORY_CHANGES_ITEM_UPDATE_API_URL,
      payload,
      {
        permission: AFFILIATES_APPROVED_FIXED_PAYMENTS_PERMISSIONS.HISTORY_CHANGES_EDIT,
        urlParams: {
          itemId,
        },
        queryParams: { merchant: payload.merchant },
      },
    ),
    async onSuccess({ queryClient, data, variables }) {
      if (!data?.history_of_fixed_payments_changes) {
        await queryClient.invalidateQueries({ queryKey: [FIXED_TRACKER_PAYMENTS_HISTORY_CHANGES_ITEM_QUERY, variables.itemId] });
        await queryClient.invalidateQueries({ queryKey: [AFFILIATE_TRACKER_FIXED_PAYMENT_QUERY] });
        return;
      }

      const historyItem = data.history_of_fixed_payments_changes.find((val) => val.id === variables.itemId);
      const newDate = isString(historyItem?.message.data.date) ? historyItem?.message.data.date : undefined;

      queryClient.setQueriesData<IFixedPaymentsHistoryChangesItemResponse>(
        { queryKey: [FIXED_TRACKER_PAYMENTS_HISTORY_CHANGES_ITEM_QUERY, variables.itemId] },
        (queryData) => {
          if (!queryData) {
            return queryData;
          }

          return {
            ...queryData,
            history_of_fixed_payments_change: {
              ...queryData.history_of_fixed_payments_change,
              payment_size: variables.history_of_fixed_payments_change.payment_size,
              description: variables.history_of_fixed_payments_change.description,
              date: newDate || queryData.history_of_fixed_payments_change.date,
            },
          };
        },
      );

      queryClient.setQueriesData<IAffiliateShowFixedPaymentResponse>(
        { queryKey: [AFFILIATE_TRACKER_FIXED_PAYMENT_QUERY] },
        (queryData) => {
          if (!queryData) {
            return queryData;
          }

          return {
            ...queryData,
            tracker: {
              ...queryData.tracker,
              history_of_fixed_payments_changes: [...data.history_of_fixed_payments_changes],
            },
          };
        },
      );

      displayPresetToast('edited');
    },
  });
}
